import type {
  FindingType,
  Severity,
  CanonicalState,
} from "@/components/finding-badges";

export interface ResolvableFinding {
  id?: string;
  title?: string | null;
  effectiveTitle?: string | null;
  description?: string | null;
  effectiveDescription?: string | null;
  what?: string | null;
  effectiveWhat?: string | null;
  why?: string | null;
  effectiveWhy?: string | null;
  type?: string | null;
  effectiveType?: string | null;
  severity?: string | null;
  effectiveSeverity?: string | null;
  state?: string | null;
  status?: string | null;
  triageStatus?: string | null;
}

/**
 * Resolves a human-readable finding title from canonical or observation data.
 * Falls back to first sentence of description, or a standard fallback.
 */
export function resolveFindingTitle(
  finding?: ResolvableFinding | null,
  fallback = "Untitled finding",
): string {
  if (!finding) return fallback;
  const rawTitle = finding.effectiveTitle ?? finding.title;
  const rawDescription =
    finding.effectiveDescription ||
    finding.description ||
    finding.effectiveWhat ||
    finding.what ||
    finding.effectiveWhy ||
    finding.why ||
    null;

  if (rawTitle && rawTitle.trim().length > 0 && rawTitle !== rawDescription) {
    return rawTitle.trim();
  }

  if (rawDescription && rawDescription.trim().length > 0) {
    const match = rawDescription.match(/^([^.!?\n]+[.!?]?)/);
    const firstSentence = match ? match[1].trim() : rawDescription.trim();
    return firstSentence.length > 120
      ? `${firstSentence.slice(0, 117).trimEnd()}…`
      : firstSentence;
  }

  return fallback;
}

/**
 * Resolves finding description from effective or raw fields.
 */
export function resolveFindingDescription(
  finding?: ResolvableFinding | null,
): string | null {
  if (!finding) return null;
  return (
    finding.effectiveDescription ||
    finding.description ||
    finding.effectiveWhat ||
    finding.what ||
    finding.effectiveWhy ||
    finding.why ||
    null
  );
}

/**
 * Resolves finding type (helps/hinders) from effective or raw fields.
 */
export function resolveFindingType(
  finding?: ResolvableFinding | null,
  defaultType: FindingType = "hinders",
): FindingType {
  if (!finding) return defaultType;
  return (finding.effectiveType ?? finding.type ?? defaultType) as FindingType;
}

/**
 * Resolves finding severity (critical/major/minor/observation).
 */
export function resolveFindingSeverity(
  finding?: ResolvableFinding | null,
  defaultSeverity: Severity = "nit",
): Severity {
  if (!finding) return defaultSeverity;
  return (finding.effectiveSeverity ??
    finding.severity ??
    defaultSeverity) as Severity;
}

/**
 * Resolves lifecycle state (new, open, fix_applied, snoozed, risk_accepted, etc.)
 */
export function resolveFindingState(
  finding?: ResolvableFinding | null,
): CanonicalState {
  if (!finding) return "open";
  if (finding.state) return finding.state as CanonicalState;
  const ts = finding.status ?? finding.triageStatus;
  if (ts === "addressed") return "fix_applied";
  if (ts === "deferred") return "snoozed";
  if (ts === "wont_fix") return "risk_accepted";
  if (ts === "dismissed") return "dismissed";
  return (ts as CanonicalState) ?? "open";
}
